
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEmailConfirmation } from "./useEmailConfirmation";
import { validateFormData, sanitizeInput } from "@/utils/validation";

export const useAuthentication = (
  email: string, 
  password: string, 
  username: string, 
  lastName: string,
  firstName: string,
  residenceCountry: string,
  isSignUp: boolean,
  setLoading: (loading: boolean) => void
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { handleResendConfirmation, renderResendButton } = useEmailConfirmation(email, setLoading);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    console.log("Starting authentication process...");

    // Sanitize and validate inputs
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedUsername = sanitizeInput(username);
    const sanitizedFirstName = sanitizeInput(firstName);
    const sanitizedLastName = sanitizeInput(lastName);

    // Validate form data
    const validation = validateFormData(
      sanitizedEmail, 
      password, 
      sanitizedUsername, 
      sanitizedFirstName, 
      sanitizedLastName, 
      isSignUp
    );

    if (!validation.isValid) {
      toast({
        title: "Erreur de validation",
        description: validation.errors.join(", "),
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        console.log("Processing signup - storing data for payment", { email: sanitizedEmail });
        
        // NE PAS créer l'utilisateur dans Supabase
        // Stocker les données pour le paiement
        const registrationData = {
          email: sanitizedEmail,
          username: sanitizedUsername,
          firstName: sanitizedFirstName,
          lastName: sanitizedLastName,
          residenceCountry: residenceCountry,
        };
        
        localStorage.setItem('pendingRegistration', JSON.stringify(registrationData));
        
        toast({
          title: "✅ Informations enregistrées !",
          description: "Redirection vers la page de paiement...",
          duration: 3000,
        });
        
        // Rediriger vers la page de paiement
        setTimeout(() => {
          navigate("/pricing");
        }, 1000);
        
      } else {
        console.log("Attempting login with:", { email: sanitizedEmail });
        const { data, error } = await supabase.auth.signInWithPassword({
          email: sanitizedEmail,
          password,
        });

        if (error) {
          console.error("Login error:", error);
          if (error.message.includes("Invalid login credentials")) {
            toast({
              title: "Erreur de connexion",
              description: "Email ou mot de passe incorrect.",
              variant: "destructive",
            });
            return;
          }
          if (error.message.includes("Email not confirmed")) {
            toast({
              title: "Paiement non finalisé",
              description: "Veuillez finaliser votre inscription en effectuant le paiement.",
              variant: "destructive",
            });
            // Redirect to pricing
            navigate("/pricing");
            return;
          }
          throw error;
        }

        console.log("Login successful:", data);
        // Rediriger vers la page d'accueil
        navigate("/");
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    handleAuth,
    handleResendConfirmation
  };
};
